"use client";

import Image from "next/image";
import { SkillProps } from "@/types/skill/skill.type";
import {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
  useMemo,
} from "react";
import { Tooltip } from "react-tooltip";
import { createPortal } from "react-dom";
import { useIsClient } from "@/hooks/shared/useIsClient";

// Context to share the active orbit scale with the (memo-able) Skill
// component without prop-drilling. Icons size themselves continuously
// from this so they always fit their (also-scaled) ring — no more
// bucket-fixed Tailwind sizes that overflow the orbit on narrow
// viewports.
const OrbitScaleContext = createContext<number>(1);

// How far the orbital plane is tilted toward the viewer. Higher = more
// pronounced "ringed planet" look (rings collapse toward an ellipse).
const TILT_DEG = 58;
const TILT_RAD = (TILT_DEG * Math.PI) / 180;

// CSS perspective on the outer container. Also used in the icon scale
// math to invert the perspective magnification per-icon (so icons stay
// near-uniform in visible size regardless of their orbital depth).
const PERSPECTIVE_PX = 1200;

// Shift each orbit's center upward by this fraction of its own radius.
// Effect: front (bottom) of the rings are pulled closer together,
// back (top) of the rings are spread further apart. 0 = concentric,
// 0.3 = pretty dramatic. Tuned to "a little bit" at 0.15.
const ORBIT_FRONT_BIAS = 0.15;

// Outer ring (highlight 1) uses a stronger bias than the inner/middle
// rings, so its center is pulled further up. Effect: at the FRONT
// (bottom of the ellipse) the outer ring sits closer to the middle
// ring (smaller front gap), while at the BACK (top) it fans further
// away (bigger back gap) — exactly the front-narrow / back-wide look
// requested. With dynamic icon sizing in place, 0.20 is the sweet
// spot: visibly compresses the front gap without ever causing icon
// overlap (cap before overlap is ~0.224 with the current outerGap).
const OUTER_RING_BIAS = 0.2;
const getRingShift = (radius: number, highlight: number) => {
  const bias = highlight === 1 ? OUTER_RING_BIAS : ORBIT_FRONT_BIAS;
  return radius * bias;
};

// Icon sizing — continuous, scaled by orbitScale. At scale=1 these
// pixel values match the previous "large bucket" Tailwind classes
// (h1: w-12 + p-2, h2: w-10 + p-1.5, h3: w-8 + p-1) so desktop is
// visually unchanged. On narrower viewports both the orbit and the
// icons shrink together, preserving the ring-to-icon ratio and
// keeping the front-gap clearance constant at every viewport size.
type IconSize = {
  imagePx: number;
  padPx: number;
  textClass: string;
};

const getIconSize = (highlight: number, scale: number): IconSize => {
  let imgPx: number;
  let padPx: number;
  let textClass: string;
  switch (highlight) {
    case 1:
      imgPx = 48;
      padPx = 8;
      textClass = "text-lg";
      break;
    case 2:
      imgPx = 40;
      padPx = 6;
      textClass = "text-base";
      break;
    case 3:
      imgPx = 32;
      padPx = 4;
      textClass = "text-sm";
      break;
    default:
      imgPx = 36;
      padPx = 6;
      textClass = "text-base";
  }
  // Floor each dimension at a sane minimum so icons stay readable
  // and the wrapper never collapses to 0 padding on extreme widths.
  return {
    imagePx: Math.max(10, Math.round(imgPx * scale)),
    padPx: Math.max(2, Math.round(padPx * scale)),
    textClass,
  };
};

// Outer ring's radius at scale=1 (140 + 120 + 180 = 440 — matches
// the math in getOrbitRadius below). Used in SkillClient to derive a
// fit scale from the measured container width.
const BASE_OUTER_RADIUS = 440;

// Outer-icon visible reach at scale=1 (h1 wrapper half = 32, plus a
// few px slack for the visualScale=1.1 lift at the ring's vertical
// extremes). Total reach = orbitScale * (BASE_OUTER_RADIUS + ICON_HALF).
const ICON_HALF_AT_SCALE_1 = 32;

const getOrbitRadius = (highlight: number, scale: number) => {
  // Wider gaps = more arc length per icon (less crowding) AND more
  // ring-to-ring breathing room.
  // History: 80/75/80 → 110/95/100 → 140/120/125 → 140/120/140 →
  //          140/120/200 → 140/120/180 (current). The outer gap was
  //          the dominant source of front-side overlap on small/
  //          medium devices, so we widened it; 180 is the smallest
  //          value that still keeps a positive front-edge clearance
  //          at every viewport with the current dynamic icon sizing.
  const baseRadius = 140 * scale; // innermost orbit (highlight 3)
  const innerGap = 120 * scale; // gap from highlight 3 → 2
  const outerGap = 180 * scale; // gap from highlight 2 → 1
  switch (highlight) {
    case 3:
      return baseRadius; // Smallest skills closest to center
    case 2:
      return baseRadius + innerGap; // Medium skills in middle orbit
    case 1:
      return baseRadius + innerGap + outerGap; // Largest skills farthest out
    default:
      return baseRadius + innerGap;
  }
};

const Skill = ({ name, icon, highlight }: SkillProps) => {
  // Pull the live orbit scale so this icon auto-sizes with its ring.
  const orbitScale = useContext(OrbitScaleContext);
  const { imagePx, padPx, textClass } = getIconSize(highlight, orbitScale);

  // Tooltip target only — the actual <Tooltip> renderer lives in
  // SkillClient and is portal'd into <body> so 3D parent transforms
  // don't warp its position.
  // Note: only `transform` is transitioned (not `all`) so we don't
  // animate any incidental property like box-shadow.
  return (
    <div
      data-tooltip-id="skill-tooltip"
      data-tooltip-content={name}
      className={`flex flex-col items-center justify-center gap-2 bg-gray-200 rounded-full ${textClass} transition-transform duration-300 hover:scale-110`}
      style={{ padding: `${padPx}px` }}
    >
      <Image
        width={imagePx}
        height={imagePx}
        src={icon}
        alt={`${name} icon`}
        className="rounded-2xl object-contain"
        style={{ width: imagePx, height: imagePx }}
      />
    </div>
  );
};

const SkillClient = ({ skillsData }: { skillsData: SkillProps[] }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [time, setTime] = useState(0);
  // Measured container width drives the continuous orbit/icon scale.
  // 0 means "not measured yet" (SSR / initial render); we fall back
  // to scale=1 in that case and the post-mount ResizeObserver will
  // settle the real value on hydrate.
  const [containerWidth, setContainerWidth] = useState(0);

  // Gate the body-portal'd tooltip on client mount so SSR markup matches.
  const mounted = useIsClient();

  // Track which orbital icon is currently hovered so we can imperatively
  // bump it to the top of the stack. Going through React state (rather
  // than a Tailwind `hover:` utility) sidesteps inline-style vs. class
  // specificity issues — the z-index swap is instant and deterministic,
  // which kills the "clipping for a second" flash on hover-in.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // 3D star particles scattered behind the orbital system. Each star has
  // a random translateZ depth so the perspective container parallaxes
  // them naturally. Generated once with a deterministic pseudo-random
  // (sine-hash) and rounded to fixed precision so the string Next.js
  // renders on the server (e.g. `2.225px`) matches the client's runtime
  // output exactly — eliminates the inline-style hydration warning.
  const stars = useMemo(() => {
    const rand = (seed: number) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x); // 0..1
    };
    const round = (n: number) => Math.round(n * 1000) / 1000;
    return Array.from({ length: 60 }, (_, i) => ({
      x: round((rand(i * 12.9898 + 1) - 0.5) * 800),
      y: round((rand(i * 78.233 + 2) - 0.5) * 600),
      z: round(-rand(i * 37.719 + 3) * 500 + 60),
      size: round(1 + rand(i * 91.13 + 4) * 2),
      opacity: round(0.25 + rand(i * 53.91 + 5) * 0.55),
    }));
  }, []);

  // Measure the container (not the window)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const setFromWidth = (w: number) => {
      setContainerWidth(w);
    };

    // initial measurement
    setFromWidth(el.clientWidth);

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setFromWidth(entry.contentRect.width);
      }
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Continuous scale clamped to fit the measured container. Both the
  // ring radii AND the icon dimensions scale with this value, so the
  // outer icon's edge sits at: orbitScale * (BASE_OUTER_RADIUS + ICON_HALF).
  // SAFETY_PX is just visual breathing room from the container edge.
  // ORBIT_FLOOR keeps icons readable on extreme-narrow widths (under
  // it, icons would shrink to <10px). On a 320px phone the floor
  // causes a ~4px overflow; on 360px+ the floor never kicks in.
  const SAFETY_PX = 8;
  const ORBIT_FLOOR = 0.3;
  const orbitScale = (() => {
    if (containerWidth === 0) return 1;
    const baseReach = BASE_OUTER_RADIUS + ICON_HALF_AT_SCALE_1;
    const fitScale = (containerWidth / 2 - SAFETY_PX) / baseReach;
    return Math.min(1, Math.max(ORBIT_FLOOR, fitScale));
  })();

  // Center logo sizes track the orbit, but with a piecewise curve.
  // Above 0.5 we keep the full 80/72 design sizes (matches desktop).
  // Below 0.5 we shrink the center more aggressively than the orbit
  // does, because the inner-ring radius compresses faster than the
  // icon shrinks (the icon has a non-zero floor) — without this, the
  // BW logo would graze the inner ring's icons on the very narrowest
  // phones.
  const globeSize =
    orbitScale >= 0.5
      ? Math.round(orbitScale * 80)
      : Math.round(orbitScale * 60);
  const bwSize =
    orbitScale >= 0.5
      ? Math.round(orbitScale * 72)
      : Math.round(orbitScale * 48);

  useEffect(() => {
    const interval = setInterval(() => {
      setTime((prev) => prev + 0.005); // Slower animation
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Group skills by highlight level
  const skillsByHighlight = skillsData.reduce((acc, skill) => {
    acc[skill.highlight] = acc[skill.highlight] || [];
    acc[skill.highlight].push(skill);
    return acc;
  }, {} as Record<number, SkillProps[]>);

  return (
    <OrbitScaleContext.Provider value={orbitScale}>
      <div
        ref={containerRef}
        className="relative w-full h-[300px] min-[445px]:h-[400px] min-[640px]:h-[600px] flex items-center justify-center"
        style={{ perspective: `${PERSPECTIVE_PX}px` }}
      >
        {/* 3D STAR FIELD: scattered dots at varying translateZ depths so
            the perspective container parallaxes them. Lives in its own
            preserve-3d layer, sibling of the tilted stage, so the stars
            stay anchored to screen space (they don't tilt with orbits). */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ transformStyle: "preserve-3d", zIndex: 1 }}
        >
          {stars.map((star, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: `${star.size}px`,
                height: `${star.size}px`,
                transform: `translate3d(${star.x}px, ${star.y}px, ${star.z}px)`,
                opacity: star.opacity,
              }}
            />
          ))}
        </div>

        {/* CENTER GLOW: soft purple halo behind the globe/BW, sized
            relative to the globe so it scales with orbitScale. Sits
            in its own layer at zIndex 2 — above the stars but BELOW
            the tilted stage (zIndex 5) and the center logo container
            (zIndex 25). That ordering means the orbit icons and the
            globe both paint ON TOP of the glow, so the halo only
            shows in the empty space between them and never tints
            the icons (which was the issue the previous box-shadow
            implementation had). */}
        <div
          aria-hidden
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: 2 }}
        >
          <div
            className="absolute rounded-full"
            style={{
              width: globeSize * 2.6,
              height: globeSize * 2.6,
              background:
                "radial-gradient(circle, rgba(132,109,255,0.55) 0%, rgba(132,109,255,0.20) 45%, transparent 75%)",
              filter: "blur(22px)",
            }}
          />
        </div>

        {/* TILTED 3D STAGE: rings + skill icons live here. The rotateX
            collapses the rings into ellipses and gives icons a real
            sense of depth as they orbit. preserve-3d ensures children's
            transforms aren't flattened onto the stage's plane. */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `rotateX(${TILT_DEG}deg)`,
            transformStyle: "preserve-3d",
            zIndex: 5,
          }}
        >
          <svg
            className="absolute"
            style={{ width: "100%", height: "100%", overflow: "visible" }}
          >
            {([1, 2, 3] as const).map((highlight) => {
              const r = getOrbitRadius(highlight, orbitScale);
              // Bigger rings shift further up than smaller ones, so
              // the front sides of the rings pull together and the
              // back sides fan out. The outer ring uses an even
              // stronger bias (see getRingShift) for an extra
              // compressed front + extra fanned back.
              const shift = getRingShift(r, highlight);
              return (
                <circle
                  key={highlight}
                  cx="50%"
                  cy="50%"
                  r={r}
                  transform={`translate(0 ${-shift})`}
                  fill="none"
                  stroke="#d1d5db"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
              );
            })}
          </svg>

          {([1, 2, 3] as const).map((highlight) => {
            const skills = skillsByHighlight[highlight] || [];
            return skills.map((skill, index) => {
              const radius = getOrbitRadius(highlight, orbitScale);
              // Negate time for highlight 2 to rotate counterclockwise
              const angle =
                (index * 2 * Math.PI) / Math.max(skills.length, 1) +
                (highlight === 2 ? -time : time);
              // Shift this ring's center upward in plane Y. Bigger
              // radii shift more (proportional), creating the front-
              // narrow / back-fanning effect; the outer ring also
              // uses a stronger bias for extra front compression.
              const ringShift = getRingShift(radius, highlight);
              // Round to 3 decimals before composing the inline transform
              // string. Without this, JS's full-precision toString
              // disagrees with how Next.js serializes inline styles on
              // the server, producing a hydration mismatch warning.
              const round3 = (n: number) => Math.round(n * 1000) / 1000;
              const x = round3(radius * Math.cos(angle));
              const y = round3(radius * Math.sin(angle) - ringShift);

              // depth: -1 (back of orbit) → +1 (front of orbit).
              const depth = Math.sin(angle);

              // The icon's screen-space z, derived from the composed
              // transform stack:
              //   parent rotateX(TILT) maps (x, y, 0) →
              //     (x, y·cos(TILT), y·sin(TILT))
              //   icon's own translateZ(10) (after counter-rotate) adds +10.
              const iconZ = y * Math.sin(TILT_RAD) + 10;

              // CSS perspective magnifies front icons ( P/(P-z) ) and
              // shrinks back ones — which made the front of the outer
              // ring (radius 385 + tilt) blow up by ~1.39× before any
              // depth scale even applied. We compensate by dividing
              // by that magnification, so the *visible* size of every
              // icon is just `visualScale` below.
              const perspectiveMag =
                PERSPECTIVE_PX / (PERSPECTIVE_PX - iconZ);

              // Subtle 3D hint: front 10% bigger, back 10% smaller,
              // sides at baseline. Tune this number to dial the
              // parallax up or down without re-introducing perspective
              // blow-up.
              const visualScale = 1 + depth * 0.1; // 0.9 .. 1.1 visible
              const scale = round3(visualScale / perspectiveMag);

              const isHovered = hoveredKey === skill.name;

              return (
                <div
                  key={skill.name}
                  className="absolute"
                  onMouseEnter={() => setHoveredKey(skill.name)}
                  onMouseLeave={() =>
                    setHoveredKey((cur) => (cur === skill.name ? null : cur))
                  }
                  style={{
                    // Counter-rotate icons (rotateX(-TILT_DEG)) so they
                    // face the camera, then translateZ(10) to lift them
                    // off the orbit ring plane and avoid z-fighting with
                    // the dashed SVG stroke.
                    transform: `translate3d(${x}px, ${y}px, 0) rotateX(-${TILT_DEG}deg) translateZ(10px) scale(${scale})`,
                    transformStyle: "preserve-3d",
                    // 30 keeps every icon above the globe (zIndex 25),
                    // so it can never visually clip an icon. Hovered
                    // icon jumps to 100 to sit above its neighbors too.
                    zIndex: isHovered ? 100 : 30,
                    transition: "transform 0.3s ease-out",
                  }}
                >
                  <Skill {...skill} />
                </div>
              );
            });
          })}
        </div>

        {/* SCREEN-ALIGNED CENTER: globe + BW logo are intentionally
            siblings of the tilted stage (not children), so the globe's
            existing `animate-spin-slow` rotateX flip runs in screen
            space and isn't composed with the stage's rotateX(58). */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ zIndex: 25 }}
        >
          {/* Globe + BW use continuous pixel sizes derived from
              orbitScale so they always stay proportional to the rings.
              On narrow viewports the orbit shrinks → these shrink too,
              keeping the inner ring clear of the center. */}
          <Image
            src="/assets/logo/only-globe-white.svg"
            alt="Center logo"
            width={globeSize}
            height={globeSize}
            style={{ width: globeSize, height: globeSize }}
            className="absolute object-cover rounded-full transition-all duration-300 animate-spin-slow"
          />

          <Image
            src="/assets/logo/bw.svg"
            alt="Center logo"
            width={bwSize}
            height={bwSize}
            style={{ width: bwSize, height: bwSize }}
            className="absolute object-contain rounded-2xl bg-transparent transition-all duration-300 hover:scale-110"
          />
        </div>
      </div>

      {/* Single shared tooltip rendered into <body> via portal so its
          positioning isn't warped by the 3D-transformed parents.
          NOTE: don't force `!opacity-100` here — react-tooltip v5
          drives its own show/hide via opacity, and overriding it
          breaks the visible/hidden state. */}
      {mounted &&
        createPortal(
          <Tooltip
            id="skill-tooltip"
            place="top"
            delayShow={80}
            delayHide={50}
            className="!z-[1000] !rounded-xl !bg-white !px-3 !py-1.5 !text-sm !font-medium !text-black !shadow-lg"
          />,
          document.body
        )}
    </OrbitScaleContext.Provider>
  );
};

export default SkillClient;
